import {
  type DatesRangeValue,
  DateValue,
} from '@mantine/dates/lib/types/DatePickerValue';
import {
  hideNotification,
  showNotification,
  updateNotification,
} from '@mantine/notifications';
import { IconX } from '@tabler/icons-react';
import dayjs from 'dayjs';
import React from 'react';

import type { FilePreview } from '../components/FileViewer/useFilePreviewModal';
import DocumentGeneratorService from '../services/DocumentGeneratorService';
import DocumentsService from '../services/DocumentsService';
import ExpenseReportService from '../services/ExpenseReportService';
import LeaveService from '../services/LeaveService';
import PayslipService from '../services/PayslipService';
import ShuttleSheetService from '../services/ShuttleSheetService';
import VariablePayrollService from '../services/VariablePayrollService';
import type { TranslateFunction } from '../types/front/common';
import type { FieldType, PayslipMonthResponse } from '../types/types';
import { milliToFullDateString } from './format';
import { getPersistedObject } from './localStorage';

function showFileLoadingNotification(id: string, t: TranslateFunction) {
  showNotification({
    id,
    loading: true,
    title: t('w.loading'),
    message: '',
    autoClose: false,
    withCloseButton: false,
  });
}

function hideFileLoadingNotification(id: string) {
  hideNotification(id);
}

function showFileLoadingErrorNotification(
  id: string,
  t: TranslateFunction
): void {
  updateNotification({
    id,
    loading: false,
    color: 'red',
    title: t('w.error'),
    message: '',
    icon: <IconX />,
    autoClose: 4000,
    withCloseButton: true,
  });
}

export function getMimeType(extension: string): string {
  const mimeTypes: { [key: string]: string } = {
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    png: 'image/png',
    gif: 'image/gif',
    pdf: 'application/pdf',
    doc: 'application/msword',
    docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    xls: 'application/vnd.ms-excel',
    xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    ppt: 'application/vnd.ms-powerpoint',
    pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    txt: 'text/plain',
    html: 'text/html',
    css: 'text/css',
    js: 'text/javascript',
    json: 'application/json',
    xml: 'application/xml',
    mp3: 'audio/mpeg',
    wav: 'audio/wav',
    ogg: 'audio/ogg',
    mp4: 'video/mp4',
    avi: 'video/x-msvideo',
    mpeg: 'video/mpeg',
    zip: 'application/zip',
    rar: 'application/x-rar-compressed',
    tar: 'application/x-tar',
    gz: 'application/gzip',
    '7z': 'application/x-7z-compressed',
    // Ajoutez d'autres extensions et types MIME au besoin
  };

  return mimeTypes[extension.toLowerCase()] || 'application/octet-stream'; // Si l'extension n'est pas répertoriée, retourne un type MIME par défaut
}

export function loadExpenseReportAttachment(
  id: string,
  fileName: string | undefined,
  companyId: string,
  access_token: string,
  setFile: (file: string) => void,
  t: TranslateFunction
) {
  const fileExtension: string | 'jpg' | 'jpeg' | 'png' | 'pdf' | undefined =
    fileName?.split('.').pop();
  const notificationId = `expense-report-attachment-loading-${id}`;
  showFileLoadingNotification(notificationId, t);

  let xhr = new XMLHttpRequest();
  xhr.responseType = 'blob';

  xhr.onload = function () {
    if (xhr.status === 200) {
      const newBlob = new Blob([this.response], {
        type: getMimeType(fileExtension ? fileExtension : ''),
      });
      const filePath = window.URL.createObjectURL(newBlob);
      setFile(filePath);
      hideFileLoadingNotification(notificationId);
    } else {
      showFileLoadingErrorNotification(notificationId, t);
    }
  };
  xhr.onerror = function () {
    showFileLoadingErrorNotification(notificationId, t);
  };
  xhr.open(
    'GET',
    ExpenseReportService.expenseReportAttachmentFile(companyId, id),
    true
  );
  xhr.setRequestHeader('Authorization', 'Bearer ' + access_token);
  xhr.send();
}

export function downloadDocumentGeneratorTemplate(
  id: string,
  fileName: string | undefined,
  companyId: string,
  access_token: string
) {
  const fileExtension: string | 'jpg' | 'jpeg' | 'png' | 'pdf' | undefined =
    fileName?.split('.').pop();

  let xhr = new XMLHttpRequest();
  xhr.responseType = 'blob';

  xhr.onload = function () {
    if (xhr.status === 200) {
      const newBlob = new Blob([this.response], {
        type: getMimeType(fileExtension ? fileExtension : ''),
      });
      const filePath = window.URL.createObjectURL(newBlob);
      let link = document.createElement('a');
      link.href = filePath;
      link.download = fileName ? fileName : '';
      link.target = '_blank';
      link.click();
    }
  };
  xhr.open(
    'GET',
    DocumentGeneratorService.getTemplateFile(companyId, id),
    true
  );
  xhr.setRequestHeader('Authorization', 'Bearer ' + access_token);
  xhr.send();
}

/**
 * Les aperçus de ce module ne sont volontairement pas ouverts via `modals.open` : l'écran
 * appelant peut lui-même être affiché en modale (import des bulletins d'un client côté
 * gestionnaire de paie, détail d'une note de frais…), et empiler une modale sur la pile de
 * Mantine démonterait cet écran, qui repartirait à zéro à la fermeture de l'aperçu.
 * L'appelant fournit donc `openFilePreview`, issu de {@link useFilePreviewModal}, qui rend
 * l'aperçu dans son propre arbre React.
 */
export function handleOpenPayslipPdfFile(
  userId: string,
  fullname: string,
  selectedDate: DateValue | DatesRangeValue | Date[],
  payslipsMonthInfo: PayslipMonthResponse | undefined,
  access_token: string,
  t: TranslateFunction,
  openFilePreview: (preview: FilePreview) => void
) {
  const gma = getPersistedObject('gma');
  // @ts-ignore
  const month = dayjs(selectedDate).month() + 1;
  // @ts-ignore
  const year = dayjs(selectedDate).year();
  const notificationId = `payslip-${userId}-${month}-${year}-loading`;
  showFileLoadingNotification(notificationId, t);

  let xhr = new XMLHttpRequest();
  xhr.responseType = 'blob';
  xhr.onload = function () {
    if (xhr.status === 200) {
      const newBlob = new Blob([this.response], {
        type: 'application/pdf',
      });
      const filePath = window.URL.createObjectURL(newBlob);

      // @ts-ignore
      const title = `${fullname} - ${dayjs(selectedDate).format('MMMM YYYY')}`;

      hideFileLoadingNotification(notificationId);
      openFilePreview({
        id: `${fullname}-${month}-${year}`,
        title,
        srcUrl: filePath,
        fileName: `${fullname}-${month}-${year}.pdf`,
      });
    } else {
      showFileLoadingErrorNotification(notificationId, t);
    }
  };
  xhr.onerror = function () {
    showFileLoadingErrorNotification(notificationId, t);
  };
  xhr.open(
    'GET',
    PayslipService.getUserPayslipMonth(
      userId,
      year,
      month,
      payslipsMonthInfo?.companyId,
      gma?.access_token,
      gma?.refresh_token
    ),
    true
  );
  xhr.setRequestHeader('Authorization', 'Bearer ' + access_token);
  xhr.send();
}

/** Même contrainte d'imbrication que {@link handleOpenPayslipPdfFile}. */
export function handleOpenPayslipPdfSourceFile(
  item: { date: number },
  index: number,
  payslipMonth: PayslipMonthResponse | undefined,
  access_token: string,
  t: TranslateFunction,
  openFilePreview: (preview: FilePreview) => void
) {
  const notificationId = `payslip-source-${payslipMonth?.id}-${item.date}-loading`;
  showFileLoadingNotification(notificationId, t);

  let xhr = new XMLHttpRequest();
  xhr.responseType = 'blob';
  xhr.onload = function () {
    if (xhr.status === 200) {
      const newBlob = new Blob([this.response], {
        type: 'application/pdf',
      });
      const filePath = window.URL.createObjectURL(newBlob);
      let link = document.createElement('a');
      link.href = filePath;
      link.download = `${item.date}.pdf`;
      link.target = '_blank';

      hideFileLoadingNotification(notificationId);
      openFilePreview({
        id: `${payslipMonth?.id}-${item.date}`,
        title: milliToFullDateString(item.date),
        srcUrl: filePath,
        fileName: `${payslipMonth?.id}-${item.date}.pdf`,
        download: { label: t('w.download'), onDownload: () => link.click() },
      });
    } else {
      showFileLoadingErrorNotification(notificationId, t);
    }
  };
  xhr.onerror = function () {
    showFileLoadingErrorNotification(notificationId, t);
  };

  xhr.open(
    'GET',
    PayslipService.downloadPayslipSourceFile(
      payslipMonth ? payslipMonth.id : '',
      index,
      payslipMonth ? payslipMonth.companyId : ''
    ),
    true
  );
  xhr.setRequestHeader('Authorization', 'Bearer ' + access_token);
  xhr.send();
}

/** Même contrainte d'imbrication que {@link handleOpenPayslipPdfFile}. */
export function handleOpenShuttleSheetFile(
  companyId: string | undefined,
  shuttleSheetId: string | undefined,
  shuttleSheetName: string | undefined,
  shuttleSheetType: FieldType,
  file: File,
  access_token: string,
  t: TranslateFunction,
  openFilePreview: (preview: FilePreview) => void
): void {
  const fileExtension: string | 'jpg' | 'jpeg' | 'png' | 'pdf' | undefined =
    file.name?.split('.').pop() || '';

  if (shuttleSheetId) {
    let xhr = new XMLHttpRequest();
    xhr.responseType = 'blob';

    xhr.onload = function () {
      if (xhr.status === 200) {
        const newBlob = new Blob([this.response], {
          type: getMimeType(`${file}`.split('.').pop() || ''),
        });
        const filePath = window.URL.createObjectURL(newBlob);

        let link = document.createElement('a');
        link.href = filePath;
        link.download = shuttleSheetName ? shuttleSheetName : '';
        link.target = '_blank';

        openFilePreview({
          id: `shuttle-sheet-${shuttleSheetId}`,
          title: shuttleSheetName,
          srcUrl: filePath,
          fileName: shuttleSheetName ? shuttleSheetName : '',
          kind:
            getMimeType(`${file}`.split('.').pop() || '') === 'application/pdf'
              ? 'file'
              : 'image',
          download: { label: t('w.download'), onDownload: () => link.click() },
        });
      }
    };
    xhr.open(
      'GET',
      ShuttleSheetService.getShuttleSheetFile(
        companyId,
        shuttleSheetId,
        shuttleSheetName,
        shuttleSheetType
      ),
      true
    );
    xhr.setRequestHeader('Authorization', 'Bearer ' + access_token);
    xhr.send();
  } else {
    openFilePreview({
      id: `shuttle-sheet-new-file`,
      title: file.name,
      srcUrl: URL.createObjectURL(file),
      fileName: file.name,
      kind: getMimeType(fileExtension) === 'application/pdf' ? 'file' : 'image',
    });
  }
}

/** Même contrainte d'imbrication que {@link handleOpenPayslipPdfFile}. */
export function handleOpenDocumentFile(
  companyId: string,
  documentId: string,
  fileName: string,
  extension: string,
  access_token: string,
  t: TranslateFunction,
  openFilePreview: (preview: FilePreview) => void
): void {
  let xhr = new XMLHttpRequest();
  xhr.responseType = 'blob';

  xhr.onload = function () {
    if (xhr.status === 200) {
      const newBlob = new Blob([this.response], {
        type: getMimeType(extension),
      });
      const filePath = window.URL.createObjectURL(newBlob);

      let link = document.createElement('a');
      link.href = filePath;
      link.download = fileName;
      link.target = '_blank';

      const mimeType = getMimeType(extension);
      openFilePreview({
        id: `document-${documentId}`,
        title: `${fileName}.${extension}`,
        srcUrl: filePath,
        fileName,
        kind: mimeType === 'application/pdf' ? 'file' : 'image',
        fullScreen:
          mimeType === 'application/pdf' || mimeType.includes('image'),
        download: { label: t('w.download'), onDownload: () => link.click() },
      });
    }
  };
  xhr.open(
    'GET',
    DocumentsService.downloadDocumentFile(companyId, documentId),
    true
  );
  xhr.setRequestHeader('Authorization', 'Bearer ' + access_token);
  xhr.send();
}

export const getExtension = (fileName: string) =>
  fileName.slice(fileName.lastIndexOf('.'));

export const isPdfFile = (fileName: string) =>
  getExtension(fileName) === '.pdf';
export const isDocxFile = (fileName: string) =>
  getExtension(fileName) === '.docx';

/** Même contrainte d'imbrication que {@link handleOpenPayslipPdfFile}. */
export function handleOpenVariablePayrollFile(
  companyId: string,
  employeeId: string,
  endOfMonthId: string,
  variablePayrollId: string,
  variablePayrollValue: string,
  access_token: string,
  t: TranslateFunction,
  openFilePreview: (preview: FilePreview) => void
) {
  let xhr = new XMLHttpRequest();
  xhr.responseType = 'blob';

  xhr.onload = function () {
    if (xhr.status === 200) {
      const mimeType = getMimeType(getExtension(variablePayrollValue));
      const newBlob = new Blob([this.response], {
        type: mimeType,
      });
      const filePath = window.URL.createObjectURL(newBlob);

      let link = document.createElement('a');
      link.href = filePath;
      link.download = `export${getExtension(variablePayrollValue)}`;
      link.target = '_blank';

      openFilePreview({
        id: `document-${variablePayrollId}`,
        srcUrl: filePath,
        fileName: variablePayrollValue,
        kind: mimeType === 'application/pdf' ? 'file' : 'image',
        fullScreen:
          mimeType === 'application/pdf' || mimeType.includes('image'),
        download: { label: t('w.download'), onDownload: () => link.click() },
      });
    }
  };
  xhr.open(
    'GET',
    VariablePayrollService.getVariablePayrollFile(
      companyId,
      employeeId,
      endOfMonthId,
      variablePayrollId
    ),
    true
  );
  xhr.setRequestHeader('Authorization', 'Bearer ' + access_token);
  xhr.send();
}

/** Même contrainte d'imbrication que {@link handleOpenPayslipPdfFile}. */
export function handleOpenExpenseReportAttachmentFile(
  companyId: string,
  expenseReportId: string,
  extension: string,
  access_token: string,
  t: TranslateFunction,
  openFilePreview: (preview: FilePreview) => void
) {
  const notificationId = `expense-report-attachment-${expenseReportId}-loading`;
  showFileLoadingNotification(notificationId, t);

  let xhr = new XMLHttpRequest();
  xhr.responseType = 'blob';

  xhr.onload = function () {
    if (xhr.status === 200) {
      const mimeType = getMimeType(extension);
      const newBlob = new Blob([this.response], {
        type: mimeType,
      });
      const filePath = window.URL.createObjectURL(newBlob);
      let link = document.createElement('a');
      link.href = filePath;
      link.download = `export.${extension}`;
      link.target = '_blank';

      hideFileLoadingNotification(notificationId);
      openFilePreview({
        id: `expense-report-attachment-${expenseReportId}`,
        srcUrl: filePath,
        fileName: `export.${extension}`,
        kind: mimeType === 'application/pdf' ? 'file' : 'image',
        fullScreen:
          mimeType === 'application/pdf' || mimeType.includes('image'),
        download: { label: t('w.download'), onDownload: () => link.click() },
      });
    } else {
      showFileLoadingErrorNotification(notificationId, t);
    }
  };
  xhr.onerror = function () {
    showFileLoadingErrorNotification(notificationId, t);
  };
  xhr.open(
    'GET',
    ExpenseReportService.getAttachment(companyId, expenseReportId),
    true
  );
  xhr.setRequestHeader('Authorization', 'Bearer ' + access_token);
  xhr.send();
}

/** Même contrainte d'imbrication que {@link handleOpenPayslipPdfFile}. */
export function handleOpenLeaveAttachmentFile(
  id: string,
  leaveTypeName: string,
  userFullname: string,
  fileName: string,
  access_token: string,
  t: TranslateFunction,
  openFilePreview: (preview: FilePreview) => void
) {
  const fileExtension = fileName.split('.').pop();
  let xhr = new XMLHttpRequest();
  xhr.responseType = 'blob';

  function getBlobType() {
    switch (fileExtension) {
      case 'jpg':
      case 'jpeg':
        return 'image/jpeg';
      case 'png':
        return 'image/png';
      default:
        return 'application/pdf';
    }
  }

  xhr.onload = function () {
    if (xhr.status === 200) {
      const newBlob = new Blob([this.response], { type: getBlobType() });
      const filePath = window.URL.createObjectURL(newBlob);

      let link = document.createElement('a');
      link.href = filePath;
      link.download = `${userFullname}-${leaveTypeName}.${fileExtension}`;
      link.target = '_blank';

      openFilePreview({
        id: `${leaveTypeName}-${id}`,
        title: leaveTypeName,
        srcUrl: filePath,
        fileName: `${userFullname}-${leaveTypeName}.${fileExtension}`,
        kind: getBlobType() === 'application/pdf' ? 'file' : 'image',
        download: { label: t('w.download'), onDownload: () => link.click() },
      });
    }
  };
  xhr.open('GET', LeaveService.attachmentUrl(id), true);
  xhr.setRequestHeader('Authorization', 'Bearer ' + access_token);
  xhr.send();
}
